import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import EventSource from 'react-native-sse';
import { v4 as uuidV4 } from 'uuid';
import Config from 'utils/packages/configs';
import useAppState from 'hooks/useAppState';
import { getAccessToken } from 'middleware/api';
import { selectFeatureFlag } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getRealTimePricesSuccess } from '../actions';
import { GET_REAL_TIME_PRICES_FAILURE } from '../actions/types';
import { selectIsTradingAccountAlpaca } from '../reducers/selectors';
const url = `${Config.API_URL}/wealth/trading/assets-prices-sse`;
const useRealTimePrice = () => {
    const dispatch = useAppDispatch();
    const isAlpacaAccount = useAppSelector(selectIsTradingAccountAlpaca);
    const assetIds = useAppSelector((state) => state.unpersistedInvestReducer.realTimePricesAssetIds);
    const esRef = useRef(null);
    const currentESId = useRef(undefined);
    const appState = useAppState();
    const [shouldRetry, setShouldRetry] = useState({ retry: false });
    const shouldFetchRealTimePrices = useMemo(() => appState === 'active' || appState === 'background', [appState]);
    const [assetIdString, setAssetIdString] = useState(undefined);
    const debounceSetAssetIdString = debounce(setAssetIdString, 500);
    const hasRealTimePrices = useAppSelector((store) => selectFeatureFlag(store, 'real_time_prices').value) && isAlpacaAccount;
    useEffect(() => {
        if (hasRealTimePrices) {
            let assetIdStr;
            if (assetIds?.length) {
                const set = new Set(assetIds);
                assetIdStr = Array.from(set)
                    .sort()
                    .reduce((prev, curr, index) => prev + (index === 0 ? curr : `%2C${curr}`), '');
            }
            debounceSetAssetIdString(assetIdStr);
        }
        else {
            setAssetIdString(undefined);
        }
    }, [hasRealTimePrices, assetIds]);
    const handleData = useCallback((data, esId) => {
        if (data && esId === currentESId.current) {
            try {
                const dataObj = JSON.parse(data);
                dispatch(getRealTimePricesSuccess(dataObj));
            }
            catch {
                //
            }
        }
    }, []);
    const handleError = useCallback((esId) => {
        if (esId === currentESId.current) {
            currentESId.current = undefined;
            setShouldRetry({ retry: true });
        }
    }, []);
    useEffect(() => {
        const esId = new Date().getTime();
        currentESId.current = esId;
        if (assetIdString && shouldFetchRealTimePrices) {
            (async () => {
                const access_token = (await getAccessToken.next({ next: undefined, errorType: GET_REAL_TIME_PRICES_FAILURE })).value;
                if (access_token) {
                    const es = new EventSource(`${url}?assetIds=${assetIdString}`, {
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${access_token}`,
                            'Client-Date': new Date().toString(),
                            'X-Request-ID': uuidV4(),
                        },
                    });
                    if (esRef.current) {
                        esRef.current.removeAllEventListeners();
                        esRef.current.close();
                    }
                    esRef.current = es;
                    const listener = (event) => {
                        if (event.type === 'open') {
                            // console.log('Open SSE connection.');
                        }
                        else if (event.type === 'message') {
                            handleData(event.data, esId);
                        }
                        else if (event.type === 'error') {
                            handleError(esId);
                        }
                        else if (event.type === 'exception') {
                            handleError(esId);
                        }
                    };
                    es.addEventListener('open', listener);
                    es.addEventListener('message', listener);
                    es.addEventListener('error', listener);
                }
                // else will be logged out by other api calls
            })();
            return () => {
                if (esRef.current) {
                    esRef.current.removeAllEventListeners();
                    esRef.current.close();
                }
            };
        }
        return () => { };
    }, [handleData, assetIdString, shouldRetry, shouldFetchRealTimePrices, handleError]);
};
export default useRealTimePrice;
